import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { PushNotificationsDataService, PushNotificationsColumnsService } from '../../services';
import { DialogPushNotificationFormComponent } from './push-notification-form/push-notification-form.component';
import { DialogPushNotificationDetailsComponent } from './push-notification-details/push-notification-details.component';
import { PushNotification, PushNotificationsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-push-notifications',
  templateUrl: './push-notifications.component.html',
  styleUrls: ['./push-notifications.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class PushNotificationsComponent implements OnInit, OnDestroy {

  private pushNotificationAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private pushNotificationEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private pushNotificationDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public pushNotificationToDelete: PushNotification = {};
  public pushNotifications: PushNotification[] = [];
  public pushNotification: PushNotification = {};
  public selectedPushNotifications: PushNotification[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private pushNotificationsDataService: PushNotificationsDataService,
    private pushNotificationsColumnsService: PushNotificationsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('PushNotificationsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Push Notifications - Management');
    this.primengConfig.ripple = true;
    this.getPushNotifications();
  }

  ngOnDestroy(): void {
    console.log('PushNotificationsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getPushNotifications(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.pushNotificationsDataService.getPushNotifications(this.parameters).subscribe((response: PushNotificationsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.pushNotifications = response.results;
        } else {
          this.pushNotifications = this.pushNotifications.concat(response.results);
        }
        if (response.inlineCount > this.pushNotifications.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getPushNotifications();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHNOTIFICATIONSNOTLOADED')});
    });
  }

  importPushNotifications(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.pushNotificationsDataService.addPushNotifications(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PUSHNOTIFICATIONSCREATED')});
                if (proceed === j) {
                  this.getPushNotifications();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHNOTIFICATIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHNOTIFICATIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.pushNotificationsDataService.updatePushNotifications(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('PUSHNOTIFICATIONSUPDATED')});
                if (proceed === j) {
                  this.getPushNotifications();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHNOTIFICATIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('PUSHNOTIFICATIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.pushNotificationsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('pushNotifications.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedPushNotifications() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Push Notifications?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.pushNotificationsDataService.deletePushNotifications(this.selectedPushNotifications).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Push Notifications Deleted', life: 3000});
            this.getPushNotifications();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Notifications not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Notifications not deleted'});
        });
        this.selectedPushNotifications = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.pushNotifications, this.columns, 'PushNotifications');
  }

  exportExcel() {
    ToolsService.exportExcel(this.pushNotifications, this.columns, 'PushNotifications');
  }

  exportCsv() {
    ToolsService.exportCsv(this.pushNotifications, this.columns, 'PushNotifications');
  }

  refresh() {
    this.pushNotificationsDataService.clearCache();
    this.getPushNotifications();
  }

  pushNotificationAdd(): void {
    if (!this.navigation.getParamValue('pushNotifications.addInDialog', true)) {
      this.router.navigate(['/push-notifications/add']);
      return;
    }
    this.pushNotificationAddDialog = this.dialogService.open(DialogPushNotificationFormComponent, {
      header: '', // this.translate.instant('NEWPUSHNOTIFICATION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        pushNotification: {id: null},
        posts: this.pushNotificationsColumnsService.posts,
        videos: this.pushNotificationsColumnsService.videos,
        users: this.pushNotificationsColumnsService.users,
      },
    });

    this.pushNotificationAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'PushNotification added'});
        this.getPushNotifications();
      }
    });
  }

  pushNotificationEdit(pushNotification: PushNotification): void {
    if (!this.navigation.getParamValue('pushNotifications.editInDialog', true)) {
      this.router.navigate(['/push-notifications/edit/' + pushNotification.id]);
      return;
    }
    this.pushNotificationEditDialog = this.dialogService.open(DialogPushNotificationFormComponent, {
      header: '', // this.translate.instant('EDITPUSHNOTIFICATION') + ' ' + pushNotification.title,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        pushNotification: pushNotification,
        posts: this.pushNotificationsColumnsService.posts,
        videos: this.pushNotificationsColumnsService.videos,
        users: this.pushNotificationsColumnsService.users,
      },
    });

    this.pushNotificationEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'PushNotification updated'});
        this.getPushNotifications();
      }
    });
  }

  pushNotificationDetails(pushNotification: PushNotification): void {
    if (!this.navigation.getParamValue('pushNotifications.detailsInDialog', true)) {
      this.router.navigate(['/push-notifications/details/' + pushNotification.id]);
      return;
    }
    this.pushNotificationDetailsDialog = this.dialogService.open(DialogPushNotificationDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        pushNotification: pushNotification,
      }
    });
  }

  pushNotificationDelete(pushNotification: PushNotification): void {
    this.pushNotificationToDelete = pushNotification;
    this.messageService.clear();
    this.messageService.add({
      key: 'pushNotificationDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + pushNotification.title + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.pushNotificationsDataService.deletePushNotification(this.pushNotificationToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Push Notification deleted'});
      this.getPushNotifications();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Push Notification not deleted'});
    });
  }

  onDeleteReject(): void {
    this.pushNotificationToDelete = {};
    this.messageService.clear('pushNotificationDelete');
  }

}
